import type { ReactNode } from 'react';
import { alpha, Box, ButtonBase, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

export interface ModuleTile {
  id: string;
  label: string;
  icon: ReactNode;
  isCurrent: boolean;
  /** Opens outside the ERP (new browser tab) */
  isExternal: boolean;
  onOpen: () => void;
}

interface ModuleTilesProps {
  tiles: ModuleTile[];
  onSelect: (tile: ModuleTile) => void;
}

/**
 * Grid of module tiles used by the module switcher (desktop popover and mobile dialog).
 */
export const ModuleTiles = ({ tiles, onSelect }: ModuleTilesProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
      {tiles.map((tile) => (
        <ButtonBase
          key={tile.id}
          onClick={() => onSelect(tile)}
          aria-current={tile.isCurrent ? 'page' : undefined}
          title={tile.isExternal ? t('app.moduleSwitcher.externalHint') : undefined}
          sx={{
            position: 'relative',
            flexDirection: 'column',
            gap: 1,
            py: 2,
            px: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: tile.isCurrent ? 'primary.main' : 'divider',
            bgcolor: tile.isCurrent ? 'action.selected' : 'background.paper',
            transition: 'background-color 0.15s, border-color 0.15s',
            '&:hover': {
              bgcolor: 'action.selected',
              borderColor: 'primary.light',
            },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, tile.isCurrent ? 0.14 : 0.08),
            }}
          >
            {tile.icon}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: tile.isCurrent ? 600 : 500,
              color: tile.isCurrent ? 'primary.main' : 'text.primary',
              textAlign: 'center',
              lineHeight: 1.3,
            }}
          >
            {tile.label}
          </Typography>
          {tile.isExternal && (
            <OpenInNewIcon
              sx={{ position: 'absolute', top: 8, right: 8, fontSize: 14, color: 'text.secondary' }}
            />
          )}
        </ButtonBase>
      ))}
    </Box>
  );
};
